import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createCustomerRfq, getRfqs, normalizeRfqsList } from '@/services/demaxtoreApi';
import { useNotifications } from '@/contexts/NotificationContext';
import { primeNotificationAudio } from '@/lib/notificationSound';
import { ChevronDown, FileDown, FileText, Plus } from 'lucide-react';

function apiErrorMessage(e, fallback) {
  const d = e?.response?.data;
  const msg = d?.detail ?? d?.detay ?? d?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (typeof e?.message === 'string' && e.message.trim()) return e.message.trim();
  return fallback;
}

/** Non-empty display value, else em dash */
function dash(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  const s = String(v).trim();
  return s || '—';
}

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function rowKey(r, index) {
  const id = r?.id ?? r?._id;
  if (id != null && String(id).trim() !== '') return String(id);
  return `rfq-${typeof index === 'number' ? index : 0}`;
}

function quantityLine(r) {
  const q = r.quantity;
  if (q == null || q === '') return '—';
  const unit = r.unit != null && String(r.unit).trim() !== '' ? String(r.unit).trim() : '';
  return unit ? `${q} ${unit}` : String(q);
}

function businessEmail(r) {
  const v = r.contact_email ?? r.customer_email ?? r.email;
  return dash(v);
}

function companyDisplay(r) {
  return dash(r.company_name ?? r.customer_name);
}

function requestDetailsBody(r) {
  const v = r.request_details ?? r.description;
  return dash(v);
}

function DetailField({ label, children, wide }) {
  return (
    <div className={wide ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-800 break-words whitespace-pre-wrap">{children}</p>
    </div>
  );
}

function RfqDetailBody({ rfq }) {
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <DetailField label="Delivery location">{dash(rfq.delivery_location)}</DetailField>
      <DetailField label="Quantity">{quantityLine(rfq)}</DetailField>
      <DetailField label="Supplier type">{dash(rfq.supplier_type)}</DetailField>
      <DetailField label="Business email">{businessEmail(rfq)}</DetailField>
      <DetailField label="Company name">{companyDisplay(rfq)}</DetailField>
      <DetailField label="Contact person">{dash(rfq.contact_name)}</DetailField>
      <DetailField label="Phone">{dash(rfq.phone)}</DetailField>
      <DetailField label="Request details" wide>
        {requestDetailsBody(rfq)}
      </DetailField>
    </dl>
  );
}

function RfqAccordionRow({ rfq, rowKey: rowKeyProp, isOpen, onToggle, onDownloadPdf, isPdfBusy }) {
  const k = rowKeyProp;
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(k)}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50/80 sm:items-center"
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Hide RFQ details' : 'Show RFQ details'}
        data-testid={`rfq-row-toggle-${k}`}
      >
        <div className="min-w-0 flex-1">
          <span className="font-heading text-sm font-semibold text-slate-900 sm:text-base break-words">{dash(rfq.title)}</span>
          {rfq.category != null && String(rfq.category).trim() !== '' && (
            <p className="mt-0.5 text-xs text-slate-500">Category: {dash(rfq.category)}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          <div className="text-right">
            <p className="hidden text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:block">Created</p>
            <p className="text-xs text-slate-600 tabular-nums sm:mt-0.5">{formatDate(rfq.created_at)}</p>
          </div>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </div>
      </button>
      {isOpen && (
        <div
          className="border-t border-slate-100 bg-slate-50/60 px-4 py-4 sm:px-5"
          data-testid={`rfq-detail-${k}`}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Details</p>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              disabled={isPdfBusy}
              onClick={(e) => {
                e.stopPropagation();
                onDownloadPdf();
              }}
              data-testid={`rfq-pdf-${k}`}
            >
              <FileDown className="h-3.5 w-3.5" aria-hidden />
              {isPdfBusy ? 'PDF…' : 'Download PDF'}
            </button>
          </div>
          <RfqDetailBody rfq={rfq} />
        </div>
      )}
    </div>
  );
}

export default function RFQs() {
  const { registerActivity } = useNotifications();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newRequestDetails, setNewRequestDetails] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newDeliveryLocation, setNewDeliveryLocation] = useState('');
  const [newSupplierType, setNewSupplierType] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);
  const [pdfBusyKey, setPdfBusyKey] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadRfqs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getRfqs();
      if (!mountedRef.current) return;
      setRfqs(normalizeRfqsList(data));
    } catch (e) {
      if (!mountedRef.current) return;
      setError(apiErrorMessage(e, 'Failed to load RFQs.'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRfqs();
  }, [loadRfqs]);

  const sortedRfqs = useMemo(() => {
    return [...rfqs].sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      const na = Number.isNaN(ta) ? 0 : ta;
      const nb = Number.isNaN(tb) ? 0 : tb;
      if (nb !== na) return nb - na;
      return String(b._id ?? b.id ?? '').localeCompare(String(a._id ?? a.id ?? ''));
    });
  }, [rfqs]);

  const handleDownloadPdf = useCallback(async (rowKeyForBusy, rfq) => {
    setPdfBusyKey(rowKeyForBusy);
    try {
      const { downloadRfqPdf } = await import('@/lib/rfqPdf');
      await downloadRfqPdf(rfq);
    } catch (err) {
      console.error(err);
    } finally {
      setPdfBusyKey(null);
    }
  }, []);

  async function handleCreateRfq(e) {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) {
      setCreateError('Title is required.');
      return;
    }
    setCreateSubmitting(true);
    setCreateError('');
    try {
      const qtyRaw = newQuantity.trim();
      let quantity;
      if (qtyRaw !== '') {
        const q = Number(qtyRaw);
        if (!Number.isFinite(q)) {
          setCreateError('Quantity must be a valid number.');
          setCreateSubmitting(false);
          return;
        }
        quantity = q;
      }
      primeNotificationAudio();
      const res = await createCustomerRfq({
        title,
        description: newDescription.trim() || undefined,
        request_details: newRequestDetails.trim() || undefined,
        category: newCategory.trim() || undefined,
        currency: 'USD',
        delivery_location: newDeliveryLocation.trim() || undefined,
        supplier_type: newSupplierType.trim() || undefined,
        company_name: newCompanyName.trim() || undefined,
        contact_name: newContactName.trim() || undefined,
        phone: newPhone.trim() || undefined,
        quantity,
        unit: newUnit.trim() || undefined,
      });
      const created = res?.data && typeof res.data === 'object' ? res.data : {};
      registerActivity({
        type: 'rfq',
        entityId: created._id ?? created.id ?? `rfq-${Date.now()}`,
        title,
        createdAt: created.created_at ?? created.createdAt,
        message: 'RFQ başarıyla gönderildi.',
      });
      setNewTitle('');
      setNewDescription('');
      setNewRequestDetails('');
      setNewCategory('');
      setNewDeliveryLocation('');
      setNewSupplierType('');
      setNewCompanyName('');
      setNewContactName('');
      setNewPhone('');
      setNewQuantity('');
      setNewUnit('');
      setShowCreate(false);
      await loadRfqs();
    } catch (err) {
      setCreateError(apiErrorMessage(err, 'Could not create RFQ.'));
    } finally {
      setCreateSubmitting(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="rfqs-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">My RFQs</h1>
          <p className="text-slate-500 text-sm mt-0.5">Request for quotations from DeMaxtore</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => { setShowCreate((v) => !v); setCreateError(''); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
            data-testid="rfqs-new-toggle"
          >
            <Plus className="w-4 h-4" aria-hidden />
            New RFQ
          </button>
          <Link to="/dashboard" className="text-sm font-medium text-sky-600 hover:text-sky-700 px-2 py-2">
            Back to dashboard
          </Link>
        </div>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreateRfq}
          className="bg-white rounded-xl border border-slate-200 p-5 space-y-4"
          data-testid="rfqs-create-form"
        >
          <p className="text-sm text-slate-600">
            Submissions here are tied to your logged-in account and appear in this list immediately.
          </p>
          {createError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="rfqs-create-error">
              {createError}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="rfq-title" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Product / service (title)</label>
              <input
                id="rfq-title"
                value={newTitle}
                onChange={(ev) => setNewTitle(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="e.g. Steel plate inquiry"
                maxLength={500}
              />
            </div>
            <div>
              <label htmlFor="rfq-category" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Category (optional)</label>
              <input
                id="rfq-category"
                value={newCategory}
                onChange={(ev) => setNewCategory(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="e.g. Steel"
              />
            </div>
            <div>
              <label htmlFor="rfq-delivery" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Delivery location (optional)</label>
              <input
                id="rfq-delivery"
                value={newDeliveryLocation}
                onChange={(ev) => setNewDeliveryLocation(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Port / city / country"
              />
            </div>
            <div>
              <label htmlFor="rfq-supplier" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Supplier type (optional)</label>
              <input
                id="rfq-supplier"
                value={newSupplierType}
                onChange={(ev) => setNewSupplierType(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="e.g. Manufacturer, trader"
              />
            </div>
            <div>
              <label htmlFor="rfq-company" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Company name (optional)</label>
              <input
                id="rfq-company"
                value={newCompanyName}
                onChange={(ev) => setNewCompanyName(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="rfq-contact" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Contact person (optional)</label>
              <input
                id="rfq-contact"
                value={newContactName}
                onChange={(ev) => setNewContactName(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="rfq-phone" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Phone (optional)</label>
              <input
                id="rfq-phone"
                value={newPhone}
                onChange={(ev) => setNewPhone(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                type="tel"
              />
            </div>
            <div>
              <label htmlFor="rfq-qty" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Quantity (optional)</label>
              <input
                id="rfq-qty"
                value={newQuantity}
                onChange={(ev) => setNewQuantity(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                inputMode="decimal"
                placeholder="e.g. 100"
              />
            </div>
            <div>
              <label htmlFor="rfq-unit" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Unit (optional)</label>
              <input
                id="rfq-unit"
                value={newUnit}
                onChange={(ev) => setNewUnit(ev.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="e.g. MT, pcs"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="rfq-desc" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Description (optional)</label>
              <textarea
                id="rfq-desc"
                value={newDescription}
                onChange={(ev) => setNewDescription(ev.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Short summary"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="rfq-reqdet" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Request details (optional)</label>
              <textarea
                id="rfq-reqdet"
                value={newRequestDetails}
                onChange={(ev) => setNewRequestDetails(ev.target.value)}
                rows={4}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Full specs, quantities, incoterms…"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createSubmitting}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
              data-testid="rfqs-create-submit"
            >
              {createSubmitting ? 'Sending…' : 'Submit RFQ'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => { setShowCreate(false); setCreateError(''); }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="rfqs-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((j) => <div key={j} className="h-40 bg-white rounded-xl border border-slate-200 animate-pulse" />)}</div>
      ) : rfqs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No RFQs yet</p>
          <p className="text-slate-400 text-sm mt-3 max-w-md mx-auto leading-relaxed">
            Use <strong className="font-medium text-slate-500">New RFQ</strong> above for requests stored directly on this account.
            For RFQs from{' '}
            <a href="https://demaxtore.com/" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">
              demaxtore.com
            </a>
            , the site form must POST to the portal API{' '}
            <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">POST …/api/public/catalog-rfq</code>
            {' '}with header <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">X-DeMaxtore-Catalog-RFQ-Token</code>
            {' '}(same value as server env <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">CATALOG_RFQ_INGEST_TOKEN</code>
            ). Prefer a <strong className="font-medium text-slate-500">server-side</strong> submit (WordPress/PHP, serverless) so the token is not exposed in browser JavaScript.
          </p>
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          data-testid="rfqs-list"
        >
          <div className="hidden border-b border-slate-100 bg-slate-50/80 px-4 py-2.5 sm:grid sm:grid-cols-[1fr_auto_2.5rem] sm:items-center sm:gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Product / service</span>
            <span className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Created</span>
            <span className="sr-only">Expand</span>
          </div>
          {sortedRfqs.map((rfq, idx) => {
            const k = rowKey(rfq, idx);
            return (
              <RfqAccordionRow
                key={k}
                rfq={rfq}
                rowKey={k}
                isOpen={expandedKey === k}
                onToggle={(key) => setExpandedKey((prev) => (prev === key ? null : key))}
                onDownloadPdf={() => handleDownloadPdf(k, rfq)}
                isPdfBusy={pdfBusyKey === k}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
